import { BillingProduct, ProgramCreateParams } from '@citruscamps/citrus-client'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripeCardElementOptions, loadStripe } from '@stripe/stripe-js'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { LoadingForm } from '../../../../../components/LoadingForm/LoadingForm'
import { Title } from '../../../../../components/Title/Title'
import translations from '../../../../../translations/en.json'
import { Address } from '../../../../../utils/address'
import { PlanDescription, PlanGraphic } from './PlanDescription'

const StripeApiKey: string = process.env.REACT_APP_STRIPE_API_KEY || ''

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
  style: {
    base: {
      color: '#757575',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#6c757d',
      },
      padding: '6px',
      lineHeight: '1.5',
    },
    invalid: {
      color: '#e63757',
      iconColor: '#e63757',
    },
  },
  hidePostalCode: true,
}

interface IProps {
  initialValues: ProgramCreateParams
  product?: BillingProduct
  onPrevious?: (value?: ProgramCreateParams) => Promise<void> | void
  onSubmit: (value: ProgramCreateParams) => Promise<void> | void
}

export const SubscribeStepWithoutStripe = ({
  initialValues,
  product,
  onPrevious,
  onSubmit,
}: IProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const { values, errors, touched, handleSubmit, setFieldError, setFieldTouched, isSubmitting } =
    useFormik<ProgramCreateParams>({
      initialValues: {
        ...initialValues,
        source_token: initialValues?.source_token || undefined,
      },
      validationSchema: Yup.object(),
      onSubmit: async (value: ProgramCreateParams, { setSubmitting }) => {
        if (product?.type === 'free') {
          await onSubmit(value)
        } else if (stripe && elements) {
          const card = elements.getElement(CardElement)
          if (card) {
            const response = await stripe.createSource(card, {
              owner: {
                name: value.name,
              },
              usage: 'reusable',
            })
            if (response.error) {
              setFieldError('source_token', response.error.message)
            } else if (response.source) {
              try {
                await onSubmit({
                  ...value,
                  source_token: response.source.id,
                })
              } catch (e: any) {
                setFieldError(
                  'source_token',
                  e.message || (translations as any)['error.default_inputs_message'],
                )
              }
            } else {
              setFieldError('source_token', (translations as any)['error.default_inputs_message'])
            }
          } else {
            await onSubmit(value)
          }
        } else {
          setFieldError('source_token', (translations as any)['error.default_inputs_message'])
        }
        setSubmitting(false)
      },
    })
  const address = Address.fromGeocode(initialValues?.address)
  if (!product) {
    return <LoadingForm />
  }
  return (
    <form onSubmit={handleSubmit}>
      <Title value="Subscribe to Citrus" />
      <h4 className="mb-4">
        {product.type !== 'free'
          ? 'Add a payment method to subscribe.'
          : 'Confirm your subscription.'}
      </h4>
      <div className="card border-primary mb-3 mb-3">
        <div className="card-body p-3">
          <div className="row ">
            <div className="col-4">
              <PlanGraphic product={product} />
            </div>
            <div className="col-8 d-flex">
              <PlanDescription
                product={product}
                language={initialValues.language}
                address={address}
              />
            </div>
          </div>
        </div>
      </div>
      {product.type !== 'free' ? (
        <>
          <div className="mb-3 text-body-secondary small mb-3 text-center">
            By confirming your subscription, you allow Citrus to charge your card for future
            payments in accordance with their terms.
          </div>
          <div className="mb-3">
            <CardElement
              className={`form-control${
                touched?.source_token && !!errors?.source_token ? ' is-invalid' : ''
              }`}
              options={CARD_ELEMENT_OPTIONS}
              onBlur={() => setFieldTouched('source_token', true)}
            />
            <div className="invalid-feedback d-block">
              {touched?.source_token && errors?.source_token ? errors?.source_token : '\u00A0'}
            </div>
          </div>
        </>
      ) : null}
      <div className="mb-3 d-flex justify-content-between mt-4">
        {onPrevious && (
          <button
            type="button"
            className="btn btn-link"
            disabled={isSubmitting}
            onClick={() => onPrevious(values)}
          >
            Back
          </button>
        )}
        {product.type !== 'free' && (
          <LoadingButton
            type="button"
            className="btn btn-link d-none d-md-inline-block ms-auto"
            disabled={isSubmitting}
            onClick={async () => {
              await onSubmit(values)
            }}
          >
            Skip for now
          </LoadingButton>
        )}
        <LoadingButton
          type="submit"
          className="btn btn-primary ms-md-2 ms-auto"
          loading={isSubmitting}
        >
          {product.type !== 'free' ? 'Start my trial' : 'Subscribe'}
        </LoadingButton>
      </div>
      {product.type !== 'free' && (
        <div className="mb-3 d-block d-md-none text-center">
          <LoadingButton
            type="button"
            className="btn btn-link"
            disabled={isSubmitting}
            onClick={async () => {
              await onSubmit(values)
            }}
          >
            Skip for now
          </LoadingButton>
        </div>
      )}
    </form>
  )
}

export const SubscribeStep = (props: IProps) => {
  const [stripePromise] = useState(() => loadStripe(StripeApiKey))

  return (
    <Elements stripe={stripePromise}>
      <SubscribeStepWithoutStripe {...props} />
    </Elements>
  )
}
